import {
  RollLogBlock,
  type RollLogEntry,
} from "@thilke/ui/blocks/roll-log-block";
import { LancerMechPanel } from "@thilke/ui/components/lancer-mech-panel";
import { Maximize2, Minimize2, PanelsTopLeft, ScrollText } from "lucide-react";
import { useEffect, useState } from "react";
import { useBlock } from "../state/graph-hooks";
import type { BlockNode } from "../state/graph-types";
import { useInteractionStore } from "../state/interaction-store";
import type { DiceRollVisibility } from "../state/type-registry";
import { type LancerPanelMode, useUIStore } from "../state/ui-store";
import { useYDoc } from "../state/yjs-provider";
import { IconButton } from "./icon-button";

interface LancerActorData {
  blockId: string;
  name: string;
  system: string;
  hp: number;
  maxHp: number;
  heat: number;
  maxHeat: number;
  structure: number;
  maxStructure: number;
  stress: number;
  maxStress: number;
  repairs: number;
  maxRepairs: number;
  burn: number;
  maxBurn: number;
  stats: {
    id: string;
    label: string;
    value: number;
    tone?: "accent" | "info" | "muted";
  }[];
}

const SCENE_TIMELINE_EDGE_KIND = "scene-timeline";
const DOC_ROOT_ID = "__document_root__";

function extractLancerData(
  blockId: string,
  block: (BlockNode & { id: string }) | null,
  typeName: string,
): LancerActorData | null {
  if (!block) return null;

  const props = (block.properties ?? {}) as Record<string, unknown>;
  const num = (key: string, fallback = 0) => {
    const v = props[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const parsed = Number(v);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
  };

  const hp = num("hp", 0);
  const maxHp = num("maxHp", hp);

  return {
    blockId,
    name: block.name || "Mech",
    system: typeName === "npc" ? "NPC" : "Mech",
    hp,
    maxHp,
    heat: num("heat", 0),
    maxHeat: num("maxHeat", 12),
    structure: num("structure", maxHp > 0 ? 4 : 0),
    maxStructure: num("maxStructure", 4),
    stress: num("stress", maxHp > 0 ? 4 : 0),
    maxStress: num("maxStress", 4),
    repairs: num("repairs", 0),
    maxRepairs: num("maxRepairs", 4),
    burn: num("burn", 0),
    maxBurn: num("maxBurn", 6),
    stats: [
      { id: "evasion", label: "Evasion", value: num("evasion", 10) },
      {
        id: "armor",
        label: "Armor",
        value: num("armor", 0),
        tone: "accent",
      },
      { id: "edef", label: "E-Def", value: num("edef", 8) },
      { id: "speed", label: "Speed", value: num("speed", 5), tone: "info" },
      { id: "save", label: "Save", value: num("save", 12) },
      {
        id: "sensor",
        label: "Sensor",
        value: num("sensor", 10),
        tone: "muted",
      },
    ],
  };
}

function useRollLogEntries(doc: any): RollLogEntry[] {
  const [entries, setEntries] = useState<RollLogEntry[]>([]);

  useEffect(() => {
    const nodesMap = doc.getMap("nodes");
    const edgesMap = doc.getMap("edges");

    const read = () => {
      const linkedIds = new Set<string>();
      for (const [, edgeMap] of edgesMap.entries()) {
        const edge = (edgeMap as any).toJSON?.() as
          | {
              source_id?: string;
              target_id?: string;
              type?: string;
              payload?: Record<string, unknown>;
            }
          | undefined;
        if (!edge?.source_id || !edge?.target_id) continue;
        if (edge.source_id !== DOC_ROOT_ID) continue;
        if (edge.type !== "reference" && edge.type !== "custom") continue;
        if ((edge.payload as any)?.kind !== SCENE_TIMELINE_EDGE_KIND) continue;
        linkedIds.add(edge.target_id);
      }

      const next: RollLogEntry[] = [];
      linkedIds.forEach((id) => {
        const nodeMap = nodesMap.get(id) as any;
        if (!nodeMap) return;
        const node = nodeMap.toJSON?.();
        if (node?.type !== "chat-event") return;
        const meta = node?.metadata ?? {};
        if (meta.entryKind !== "dice-roll") return;
        next.push({
          id: node.id,
          expression: meta.diceExpression ?? "",
          result: meta.diceResult ?? 0,
          breakdown: meta.diceBreakdown,
          rolls: meta.diceRolls,
          speaker: meta.speaker,
          when: meta.createdAt,
          visibility: meta.diceVisibility ?? "public",
        });
      });

      next.sort((a, b) => (a.when ?? 0) - (b.when ?? 0));
      setEntries(next);
    };

    read();
    edgesMap.observeDeep(read);
    nodesMap.observeDeep(read);

    return () => {
      edgesMap.unobserveDeep(read);
      nodesMap.unobserveDeep(read);
    };
  }, [doc]);

  return entries;
}

function filterByVisibility(
  entries: RollLogEntry[],
  visibility: DiceRollVisibility,
): RollLogEntry[] {
  if (visibility === "gm-only") return entries;
  return entries.filter(
    (entry) => entry.visibility === "public" || entry.visibility === undefined,
  );
}

/** Wrapper that safely handles the case where no block is selected. */
function LancerSheetContent({ blockId }: { blockId: string }) {
  const block = useBlock<BlockNode & { id: string }>(blockId);
  const lancerData = extractLancerData(blockId, block, block?.type ?? "");

  if (!lancerData) {
    return (
      <div className="text-white/30 text-[10px] mt-2">
        Select a token to view the character sheet.
      </div>
    );
  }

  return (
    <div className="tlr-lancer-sheet" data-theme="lancer-dark">
      <LancerMechPanel
        name={lancerData.name}
        system={lancerData.system}
        hp={{
          id: "hp",
          label: "HP",
          value: lancerData.hp,
          max: lancerData.maxHp,
        }}
        heat={{
          id: "heat",
          label: "Heat",
          value: lancerData.heat,
          max: lancerData.maxHeat,
        }}
        structure={{
          id: "structure",
          label: "Structure",
          value: lancerData.structure,
          max: lancerData.maxStructure,
        }}
        stress={{
          id: "stress",
          label: "Stress",
          value: lancerData.stress,
          max: lancerData.maxStress,
        }}
        repairs={{
          value: lancerData.repairs,
          max: lancerData.maxRepairs,
        }}
        burn={{
          value: lancerData.burn,
          max: lancerData.maxBurn,
        }}
        stats={lancerData.stats}
      />
    </div>
  );
}

export function LancerPanel({
  currentVisibility,
}: {
  currentVisibility: DiceRollVisibility;
}) {
  const selection = useInteractionStore((s) => s.selection);
  const setSelection = useInteractionStore((s) => s.setSelection);
  const doc = useYDoc();
  const lancerPanelMode = useUIStore((s) => s.lancerPanelMode);
  const setLancerPanelMode = useUIStore((s) => s.setLancerPanelMode);
  const lancerPoppedOut = useUIStore((s) => s.lancerPoppedOut);
  const toggleLancerPoppedOut = useUIStore((s) => s.toggleLancerPoppedOut);

  const selectedId = selection.length > 0 ? selection[0] : null;
  const allRollEntries = useRollLogEntries(doc);
  const rollEntries = filterByVisibility(allRollEntries, currentVisibility);

  const modeButtons: {
    id: LancerPanelMode;
    label: string;
    icon: typeof PanelsTopLeft;
  }[] = [
    { id: "sheet", label: "Sheet", icon: PanelsTopLeft },
    { id: "rolllog", label: "Rolls", icon: ScrollText },
  ];

  const handleEntryClick = (entryId: string) => {
    setSelection([entryId]);
  };

  return (
    <div className="flex flex-col h-full gap-2">
      {/* Mode toggle + pop button */}
      <div className="flex items-center gap-px">
        {modeButtons.map((mode) => {
          const Icon = mode.icon;
          return (
            <IconButton
              key={mode.id}
              tooltip={mode.label}
              className={`w-6 h-5 rounded flex items-center justify-center transition-colors ${
                lancerPanelMode === mode.id
                  ? "text-white/90 bg-white/12"
                  : "text-white/40 hover:text-white/70 hover:bg-white/8"
              }`}
              onClick={() => setLancerPanelMode(mode.id)}
            >
              <Icon className="w-3 h-3" />
            </IconButton>
          );
        })}
        <IconButton
          tooltip={lancerPoppedOut ? "Pop in" : "Pop out"}
          className="ml-auto w-6 h-5 rounded flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/8 transition-colors"
          onClick={toggleLancerPoppedOut}
        >
          {lancerPoppedOut ? (
            <Minimize2 className="w-3 h-3" />
          ) : (
            <Maximize2 className="w-3 h-3" />
          )}
        </IconButton>
      </div>

      {/* Content area: sheet or roll log (mutually exclusive) */}
      <div className="flex-1 overflow-y-auto">
        {lancerPanelMode === "sheet" ? (
          selectedId ? (
            <LancerSheetContent blockId={selectedId} />
          ) : (
            <div className="text-white/30 text-[10px] mt-2">
              Select a token to view the character sheet.
            </div>
          )
        ) : (
          <div data-theme="lancer-dark">
            <RollLogBlock
              entries={rollEntries}
              live
              showSpeaker
              maxHeight="none"
              className="rounded border border-white/10 bg-black/30 text-white/70"
              onEntryClick={handleEntryClick}
            />
          </div>
        )}
      </div>
    </div>
  );
}
